import React, { useEffect, useMemo, useState } from "react";
import upload_area from "../Components/Assets/upload_area.svg";
import { backend_url } from "../App";
import { adminPost, getCategories } from "../common/api/api";
import { getActiveCategories, getSubcategoriesForMain } from "../common/categoryTree";

const AdminAddProduct = () => {
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [imageUrls, setImageUrls] = useState([""]);
  const [productDetails, setProductDetails] = useState({
    name: "",
    description: "",
    mainCategory: "",
    subCategory: "",
    new_price: "",
    old_price: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    getCategories()
      .then((data) => {
        const active = getActiveCategories(data);
        setCategories(active);
        setProductDetails((prev) => ({
          ...prev,
          mainCategory: prev.mainCategory || active[0]?.slug || "",
        }));
      })
      .catch(() => setCategories([]))
      .finally(() => setCategoryLoading(false));
  }, []);

  const availableSubcategories = useMemo(
    () => getSubcategoriesForMain(categories, productDetails.mainCategory),
    [categories, productDetails.mainCategory]
  );

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    setImages((prev) => [...prev, ...files].slice(0, 10));
  };

  const removeImage = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const addProduct = async () => {
    const manualImageUrls = imageUrls.map((item) => item.trim()).filter(Boolean);
    if (!productDetails.name.trim()) return alert("Product title is required");
    if (!productDetails.mainCategory) return alert("Main category is required");
    if (!productDetails.new_price) return alert("Offer price is required");
    if (images.length === 0 && manualImageUrls.length === 0) return alert("Please add at least one image by upload or URL");

    setLoading(true);
    try {
      let uploadedUrls = [];
      if (images.length > 0) {
        const formData = new FormData();
        images.forEach((img) => formData.append("products", img));

        const uploadResp = await fetch(`${backend_url}/uploadmultiple`, {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formData,
        });
        const uploadData = await uploadResp.json();
        if (!uploadData.success) {
          setLoading(false);
          return alert("Image upload failed");
        }
        uploadedUrls = uploadData.image_urls || [];
      }

      const allImageUrls = [...uploadedUrls, ...manualImageUrls];

      const product = {
        ...productDetails,
        category: productDetails.subCategory || productDetails.mainCategory,
        image: allImageUrls[0],
        images: allImageUrls,
      };

      const data = await adminPost("/addproduct", product);
      if (data.success) {
        setSuccess(true);
        setImages([]);
        setProductDetails({
          name: "",
          description: "",
          mainCategory: categories[0]?.slug || "",
          subCategory: "",
          new_price: "",
          old_price: "",
        });
        setImageUrls([""]);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        alert(data.error || "Failed to add product");
      }
    } catch (err) {
      alert("Error: " + err.message);
    }
    setLoading(false);
  };

  const changeHandler = (e) => {
    setProductDetails({ ...productDetails, [e.target.name]: e.target.value });
  };

  const inputClass =
    "w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-4 py-2.5 text-[#e2e8f0] placeholder-[#4a5568] focus:outline-none focus:border-[#00d4ff]/60 focus:bg-white/[0.08] transition-all";
  const labelClass = "block text-sm font-medium text-[#a0aec0] mb-1.5";

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-2xl font-bold text-white mb-6">Add Product</h1>

      {success && (
        <div className="mb-5 px-4 py-3 rounded-xl bg-green-500/10 border border-green-500/30 text-green-400 text-sm font-medium">
          Product added successfully.
        </div>
      )}

      <div className="space-y-5">
        <div>
          <label className={labelClass}>Product Title <span className="text-red-400">*</span></label>
          <input type="text" name="name" value={productDetails.name} onChange={changeHandler} placeholder="e.g. Samsung Galaxy S24 Ultra" className={inputClass} />
        </div>

        <div>
          <label className={labelClass}>Product Description</label>
          <input type="text" name="description" value={productDetails.description} onChange={changeHandler} placeholder="Short product description" className={inputClass} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Original Price</label>
            <input type="number" name="old_price" value={productDetails.old_price} onChange={changeHandler} placeholder="0.00" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Offer Price <span className="text-red-400">*</span></label>
            <input type="number" name="new_price" value={productDetails.new_price} onChange={changeHandler} placeholder="0.00" className={inputClass} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Main Category <span className="text-red-400">*</span></label>
            <select
              className={inputClass}
              value={productDetails.mainCategory}
              onChange={(e) => setProductDetails({ ...productDetails, mainCategory: e.target.value, subCategory: "" })}
              disabled={categoryLoading || categories.length === 0}
            >
              {categories.map((cat) => (
                <option key={cat._id} value={cat.slug}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Subcategory</label>
            <select
              className={inputClass}
              value={productDetails.subCategory}
              onChange={(e) => setProductDetails({ ...productDetails, subCategory: e.target.value })}
              disabled={categoryLoading || availableSubcategories.length === 0}
            >
              <option value="">No subcategory</option>
              {availableSubcategories.map((sub) => (
                <option key={sub._id} value={sub.slug}>
                  {sub.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className={labelClass}>
            Product Images <span className="text-[#4a5568] font-normal text-xs">(uploads + URLs, first final image will be main)</span>
          </label>

          <div className="flex flex-wrap gap-3 mb-3">
            {images.map((file, idx) => (
              <div key={idx} className="relative w-24 h-24 rounded-xl overflow-hidden border border-white/[0.1] bg-white/[0.03] group">
                <img src={URL.createObjectURL(file)} alt="" className="w-full h-full object-cover" />
                {idx === 0 && (
                  <span className="absolute bottom-0 left-0 right-0 text-center text-[9px] font-bold bg-[rgba(0,212,255,0.8)] text-[#060c18] py-0.5">
                    MAIN
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeImage(idx)}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500/80 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer leading-none"
                >
                  x
                </button>
              </div>
            ))}
            {images.length < 10 && (
              <label
                htmlFor="file-input"
                className="flex flex-col items-center justify-center w-24 h-24 border-2 border-dashed border-white/[0.15] rounded-xl cursor-pointer hover:border-[#00d4ff]/50 hover:bg-white/[0.02] transition-all"
              >
                <img src={upload_area} alt="Upload" className="w-8 h-8 opacity-40 mb-1" />
                <span className="text-[10px] text-[#4a5568]">Add Image</span>
              </label>
            )}
          </div>

          <input onChange={handleImageChange} type="file" name="images" id="file-input" accept="image/*,.avif" multiple hidden />
          <p className="text-xs text-[#4a5568]">{images.length}/10 uploaded files selected. AI-generated JPG, PNG, WebP, and AVIF files are supported.</p>
        </div>

        <div>
          <label className={labelClass}>Image URL(s)</label>
          <div className="space-y-2">
            {imageUrls.map((url, idx) => (
              <div key={idx} className="flex gap-2">
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setImageUrls((prev) => prev.map((item, i) => i === idx ? e.target.value : item))}
                  placeholder="https://example.com/image.png"
                  className={inputClass}
                />
                {imageUrls.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setImageUrls((prev) => prev.filter((_, i) => i !== idx))}
                    className="px-3 rounded-lg border border-red-400/20 text-red-400 hover:bg-red-400/10"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setImageUrls((prev) => [...prev, ""])}
            className="mt-2 px-3 py-2 rounded-lg border border-[rgba(0,212,255,0.25)] text-[#00d4ff] text-sm hover:bg-[rgba(0,212,255,0.08)]"
          >
            + Add Image URL
          </button>
        </div>

        <button
          onClick={addProduct}
          disabled={loading || categoryLoading || categories.length === 0}
          className="px-8 py-3 bg-gradient-to-r from-[#00d4ff] to-[#0099cc] text-[#060c18] font-bold rounded-lg hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Uploading..." : "ADD PRODUCT"}
        </button>

        {!categoryLoading && categories.length === 0 && (
          <p className="text-sm text-red-400">No categories found. Please create categories first from the Categories page.</p>
        )}
      </div>
    </div>
  );
};

export default AdminAddProduct;
